import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, signal } from '@angular/core';

import {
  AboutPanelComponent,
  AboutPanelInputs,
  AuthState,
  FooterTemplate,
  NavigationItem,
  PermissionKey,
  PermissionsState,
  SetEnvironment,
  SidePanel,
  SidepanelTypesV3,
  SidePanelV3,
  UserState,
} from '@gea/digital-ui-lib';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { ActivationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from '../environments/models/environment.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImagesService } from './shared/services/images.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  // <CHANGE ME>
  readonly BLUEPRINT_APP_TITLE = 'Blu-Red Select';

  tokenReady = false;
  loggedOutPageReady = false;
  userReady = false;
  noAuth?: boolean;
  isAdminViewActive = signal<boolean>(false);
  currentLanguage = '';
  userCountry = 'US';
  headerTitle = 'Blu-Red Select';

  // <CHANGE ME> - Add the navigation items for the Blueprint App
  navItems: NavigationItem[] = [
    {
      label: 'Dashboard',
      icon: 'home',
      route: '/home',
    },
    {
      label: 'Projects',
      icon: 'list',
      route: '/projects',
    },
    {
      label: 'Products',
      icon: 'questionnaire',
      route: '/products',
    },
  ];

  adminNavItems: NavigationItem[] = [
    {
      icon: 'multiple',
      label: 'User Management',
      route: '/user-management',
    },
    {
      icon: 'settings-gear',
      label: 'Data Management',
      route: '/data-maintenance',
    },
  ];

  botItems: SidePanelV3[] = [];
  private permissions: PermissionKey[] = [];
  // <CHANGE ME> - Add the routes that do not require authentication
  readonly NO_AUTH_ROUTES: string[] = ['about-gea'];
  productIcons: string;

  get divisionIconUrl() {
    return this.environment.iconStorageURL.split('core')[0] + 'hrt/insightpartner-bluredselect/icons/blu-red-select_24px.svg';
  }

  constructor(
    private translate: TranslateService,
    private store: Store,
    private router: Router,
    private destroyed: DestroyRef,
    private imagesService: ImagesService,
    @Inject(Window) private window: Window,
    @Inject(ENVIRONMENT_CONFIG) public environment: EnvironmentConfiguration
  ) {
    this.store
      .select(UserState.token)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe(() => {
        this.tokenReady = true;
      });
    this.store
      .select(UserState.loggedOutPageReady)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((ready) => {
        this.loggedOutPageReady = !!ready;
      });
    this.store
      .select(UserState.authState)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((authState) => {
        if (authState === AuthState.LogoutRequested) {
          this.tokenReady = false;
          this.loggedOutPageReady = false;
        }
      });
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((event) => event instanceof ActivationEnd)
      )
      .subscribe((event) => {
        this.noAuth = this.NO_AUTH_ROUTES.includes(event.snapshot?.routeConfig?.path || '');
        if (event instanceof ActivationEnd) {
          this.headerTitle = this.getHeaderTitle();
        }
      });
    this.productIcons = this.imagesService.ProductIcons.portal;
  }

  ngOnInit(): void {
    if (this.window.opener && this.window.location.hash.includes('access_token')) {
      const hash = this.window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const state = params.get('state');

      if (accessToken && state) {
        this.window.opener.postMessage({ access_token: accessToken, state }, this.window.location.origin);
        this.window.close();
        return;
      }
    }

    this.checkUserPermission();

    // This is the official syntax of ngxs
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.store
      .select(UserState.user)
      .pipe(
        takeUntilDestroyed(this.destroyed),
        filter((user) => !!user.email)
      )
      .subscribe((user) => {
        if (user?.country) {
          this.userCountry = user.country;
        }
        const selectedLanguage = localStorage.getItem('language');
        if (user?.language) {
          this.translate.use(user.language);
        } else if (selectedLanguage) {
          this.translate.use(selectedLanguage);
        } else {
          this.translate.use(this.translate.defaultLang);
        }
        this.userReady = true;
      });

    if (this.environment.name) {
      this.store.dispatch(new SetEnvironment(this.environment.name));
    }
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyed)).subscribe((event: LangChangeEvent) => {
      this.currentLanguage = event.lang;
      this.botItems = this.getSidePanelItems();
    });
    this.translate.setDefaultLang('de-DE');
    this.addCookielawScriptTag();
    /*TODO: Should be set with permission's API*/
    this.isAdminViewActive.set(true);
  }

  getSidePanelItems(): SidePanelV3[] {
    return [
      {
        key: 'release-notes',
        icon: 'chat',
        label: 'Release Notes',
        type: SidepanelTypesV3.FOOTER,
      },
      {
        key: 'help-center',
        icon: 'c-info',
        label: 'Help Center',
        type: SidepanelTypesV3.RIGHTBAR,
      },
      {
        key: 'contact',
        icon: 'circle-08',
        label: 'Contact',
        type: SidepanelTypesV3.CONTACT,
        /*TODO: Need to change when support URL is available*/
        supportAppBaseUrl: this.environment.baseURL,
      },
      {
        key: 'about',
        icon: 'c-info',
        label: 'About',
        type: SidepanelTypesV3.FOOTER,
        ref: AboutPanelComponent,
        inputs: {
          copyright: 'GEA Group Services GmbH 2023',
          footers: this.getFooterItems(),
        } as AboutPanelInputs,
      },
    ] as SidePanelV3[];
  }

  getFooterItems(): FooterTemplate[] {
    return [
      {
        key: 'imprint-header',
        header: 'UI-LIB.FOOTER.IMPRINT',
        content: [
          {
            key: 'imprint',
            icon: 'pdf',
            label: 'UI-LIB.FOOTER.IMPRINT',
            action: this.openImprint.bind(this),
          },
        ],
      },
      {
        key: 'privacy-header',
        header: 'UI-LIB.FOOTER.DATAPRIVACY',
        content: [
          {
            key: 'privacy',
            icon: 'pdf',
            label: 'UI-LIB.FOOTER.DATAPRIVACY',
            action: this.openDataprivacy.bind(this),
          },
        ],
      },
      {
        key: 'terms-header',
        header: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
        content: [
          {
            key: 'terms',
            icon: 'pdf',
            label: 'UI-LIB.FOOTER.TERMS-AND-CONDITIONS',
            action: this.openTermsAndConditions.bind(this),
          },
        ],
      },
      {
        key: 'cookie-settings',
        header: 'UI-LIB.FOOTER.COOKIE-SETTINGS',
        content: [
          {
            key: 'cookies',
            icon: '',
            label: 'UI-LIB.FOOTER.COOKIES',
            action: this.openCookieSettings.bind(this),
          },
        ],
      },
    ];
  }

  addCookielawScriptTag(): void {
    const cookielawId = this.environment.cookielawId;
    if (cookielawId) {
      const script = document.createElement('script');
      script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.setAttribute('data-domain-script', cookielawId);
      const head = document.head;
      const firstScriptTag = head.querySelector('script');
      if (firstScriptTag) {
        head.insertBefore(script, firstScriptTag);
      } else {
        head.appendChild(script);
      }
    }
  }

  private checkUserPermission(): void {
    this.store
      .select(PermissionsState.userPermissions)
      .pipe(takeUntilDestroyed(this.destroyed))
      .subscribe((permissions: PermissionKey[]) => {
        this.permissions = permissions;
      });
  }

  private getHeaderTitle(): string {
    return this.BLUEPRINT_APP_TITLE;
  }

  private openDataprivacy(): void {
    this.window.open(
      this.environment.storageURL +
        'terms/1/' +
        this.userCountry +
        '/' +
        this.currentLanguage.split('-')[0].toUpperCase() +
        '/DataPrivacy.pdf',
      '_blank'
    );
  }

  private openTermsAndConditions(): void {
    this.window.open(
      this.environment.storageURL +
        'terms/1/' +
        this.userCountry +
        '/' +
        this.currentLanguage.split('-')[0].toUpperCase() +
        '/TermsAndConditions.pdf',
      '_blank'
    );
  }

  private openImprint(): void {
    this.window.open('../../assets/documents/Imprint' + this.currentLanguage + '.pdf', '_blank');
  }

  private openCookieSettings(): void {
    // this syntax is necessary here to access onetrust
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    this.window.OneTrust.ToggleInfoDisplay();
  }
}
